package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AudiobookGuidePage() {
    val colors = AppTheme.colors

    val apiBaseUrl = remember {
        when {
            window.location.port == "8081" -> "http://localhost:8080"
            window.location.hostname.contains("castly-stg.twistmena.com") -> "https://api-castly-stg.twistmena.com"
            window.location.hostname.contains("castly.twistmena.com") -> "https://api-castly.twistmena.com"
            else -> ""
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            maxWidth(1000.px)
            themeTransition()
        }
    }) {
        // Header
        H2({
            style {
                marginTop(0.px)
                marginBottom(Spacing.sm)
                fontSize(Typography.xxl)
                color(Color(colors.text))
            }
        }) { Text("Audiobook Upload Guide") }

        P({
            style {
                color(Color(colors.textMuted))
                marginBottom(Spacing.xxl)
                fontSize(Typography.base)
                lineHeight("1.6")
            }
        }) { Text("Step-by-step instructions for adding audiobooks to Castly. There are multiple ways to create and import audiobooks — choose the method that best fits your workflow.") }

        Hr({
            style {
                border(0.px)
                property("border-top", "1px solid ${colors.border}")
                marginBottom(Spacing.xxl)
            }
        })

        // Table of Contents
        GuideCard(colors, "On This Page") {
            Ol({
                style {
                    paddingLeft(20.px)
                    marginTop(Spacing.sm)
                    marginBottom(0.px)
                    lineHeight("2")
                    color(Color(colors.primary))
                }
            }) {
                Li { Text("Method 1: Create Audiobook Manually") }
                Li { Text("Method 2: Import via CSV (Bulk)") }
                Li { Text("Method 3: Import via RSS Feed") }
                Li { Text("Method 4: Import via ONIX XML") }
                Li { Text("Uploading Episode Audio Files") }
                Li { Text("Uploading Cover Images") }
                Li { Text("Publishing & Status Management") }
                Li { Text("Managing Translations") }
                Li { Text("CSV Format Reference & Sample Download") }
                Li { Text("RSS Feed Format Reference") }
            }
        }

        // Method 1: Manual Creation
        SectionHeader(colors, "1", "Method 1: Create Audiobook Manually")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("Best for adding individual audiobooks with full control over all fields.")
        }
        StepList(colors, listOf(
            "Navigate to the **Audiobooks** section from the sidebar.",
            "Click the **actions dropdown** (top-right) and select **\"Create Audiobook\"**.",
            "Fill in the required fields: **Title** (required), **Author** (required), and optionally **ISBN** (must be unique if provided).",
            "Optionally add: Reader/Narrator, Publisher, Language, Copyright Year, Release Date, Description, and Subtitle.",
            "For bilingual content, fill in both Arabic and English title/subtitle fields.",
            "Select any relevant **Tags** from the tag selector.",
            "Click **\"Create Audiobook\"** to save. The audiobook is created in **DRAFT** status.",
            "After creation, you'll be taken to the audiobook detail view where you can upload episodes, cover images, and publish when ready."
        ))

        GuideCard(colors, "New: Draft Status") {
            P({ style { color(Color(colors.text)); lineHeight("1.6"); marginTop(Spacing.sm); marginBottom(0.px) } }) {
                Text("Newly created audiobooks start in DRAFT status and are not visible to end users. Use the \"Publish\" button in the detail view when the audiobook is ready to go live.")
            }
        }

        // Method 2: CSV Import
        SectionHeader(colors, "2", "Method 2: Import via CSV (Bulk)")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("Best for importing many audiobooks at once from a spreadsheet.")
        }
        StepList(colors, listOf(
            "Navigate to the **Audiobooks** section from the sidebar.",
            "Click the **actions dropdown** and select **\"Import CSV\"**.",
            "Prepare your CSV file with the required columns: **ISBN**, **Title**, and **Author** (see format reference below).",
            "Click **\"Choose File\"** and select your CSV file.",
            "Click **\"Import\"** to start the upload.",
            "Review the import report — it shows total rows, successful imports, and any errors with row numbers.",
            "Fix any errors in your CSV and re-import if needed. Existing ISBNs will be skipped."
        ))

        GuideCard(colors, "Tip") {
            P({ style { color(Color(colors.text)); lineHeight("1.6"); marginTop(Spacing.sm); marginBottom(0.px) } }) {
                Text("Tags are automatically created from the GenreTextBisac1 (split by \"/\") and Keywords (split by \",\") columns. Duration should be in HH:MM:SS format.")
            }
        }

        // Method 3: RSS Import
        SectionHeader(colors, "3", "Method 3: Import via RSS Feed")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("Best for importing audiobooks that are already published as podcast/audiobook RSS feeds. Audio files are automatically downloaded.")
        }
        StepList(colors, listOf(
            "Navigate to the **Audiobooks** section from the sidebar.",
            "Click the **actions dropdown** and select **\"Import from RSS\"**.",
            "Enter the **RSS feed URL** in the input field.",
            "Click **\"Parse Feed\"** to preview the audiobook metadata and chapters.",
            "Review the preview: cover image, title, author, narrator, and chapter list with durations.",
            "Optionally override fields: ISBN, Author, Reader/Narrator, or Title.",
            "Click **\"Confirm Import\"** to create the audiobook.",
            "Audio files will be downloaded in the background to S3. Check the download status in the audiobook detail view."
        ))

        // Method 4: ONIX Import
        SectionHeader(colors, "4", "Method 4: Import via ONIX XML")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("Best for publishers using the ONIX standard (versions 2.1 and 3.0). Supports bulk import with duplicate detection.")
        }
        StepList(colors, listOf(
            "Navigate to the **Audiobooks** section from the sidebar.",
            "Click the **actions dropdown** and select **\"Import ONIX\"**.",
            "Upload your ONIX XML file (supports ONIX 2.1 and 3.0 formats).",
            "The system will parse the file and show a preview of detected audiobook products (ProductForm AJ).",
            "For each product, choose an action: **Skip**, **Import** (new), or **Update** (existing duplicates).",
            "Review any validation errors or field differences for duplicates.",
            "Click **\"Confirm Import\"** to process the selected products.",
            "Review the import report for results."
        ))

        // Episode Upload
        SectionHeader(colors, "5", "Uploading Episode Audio Files")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("After creating an audiobook (by any method), you need to upload the chapter/episode MP3 files. There are two methods available:")
        }

        GuideCard(colors, "Option A: Add Episodes (Recommended)") {
            P({ style { color(Color(colors.text)); lineHeight("1.6"); marginTop(Spacing.sm); marginBottom(Spacing.sm) } }) {
                Text("Best for uploading files that don't follow a specific naming convention. Gives you full control over episode order and titles.")
            }
        }
        StepList(colors, listOf(
            "Open the audiobook from the list by clicking on it.",
            "Switch to the **\"CHAPTERS\"** tab in the detail view.",
            "Click **\"Add Episodes\"** to open the bulk upload panel.",
            "Click **\"Select MP3 Files\"** and choose multiple MP3 files from your computer.",
            "Files begin uploading immediately. A progress indicator is shown for each file.",
            "Non-MP3 files and files over 500MB are automatically filtered out with a warning.",
            "**Edit titles**: Click on any episode title to edit it inline (pre-filled from filename).",
            "**Reorder episodes**: Drag episodes using the handle on the left to change their order.",
            "**Remove files**: Click the X button to remove any episode from the list.",
            "When all uploads are complete, click **\"Save Episodes\"** to create the episode records.",
            "Episodes are created in **DRAFT** status with sequential numbering based on your final order."
        ))

        GuideCard(colors, "Option B: Import Chapters (Legacy)") {
            P({ style { color(Color(colors.text)); lineHeight("1.6"); marginTop(Spacing.sm); marginBottom(Spacing.sm) } }) {
                Text("Best for files that follow the naming convention XXX_ISBN.mp3. Episodes are ordered automatically by filename.")
            }
        }
        StepList(colors, listOf(
            "Open the audiobook from the list by clicking on it.",
            "Switch to the **\"CHAPTERS\"** tab in the detail view.",
            "Click **\"Import Chapters\"** to open the import dialog.",
            "Select your MP3 files. Files must follow the naming convention: **XXX_ISBN.mp3**",
            "Example: 001_9781234567890.mp3, 002_9781234567890.mp3, etc.",
            "Optionally include a cover image: **ISBN.jpg** or **ISBN.png** (JPG takes precedence).",
            "Click **\"Upload\"** to start the import.",
            "Review the import report. Episodes are created with auto-extracted durations and sequential numbering."
        ))

        GuideCard(colors, "File Naming Convention (Import Chapters only)") {
            CodeBlock(colors, """001_9781234567890.mp3    (Chapter 1)
002_9781234567890.mp3    (Chapter 2)
003_9781234567890.mp3    (Chapter 3)
9781234567890.jpg        (Cover image - optional)""")
        }

        // Cover Images
        SectionHeader(colors, "6", "Uploading Cover Images")
        StepList(colors, listOf(
            "Open the audiobook from the list.",
            "In the **\"DATA\"** tab, find the cover image section.",
            "Click **\"Upload Cover\"** and select a JPG or PNG image.",
            "The image is uploaded to S3 and served via CDN.",
            "Alternatively, include a cover image during episode import using the ISBN.jpg/ISBN.png naming."
        ))

        // Publishing & Status
        SectionHeader(colors, "7", "Publishing & Status Management")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("Audiobooks follow a DRAFT → PUBLISHED lifecycle. Draft audiobooks are only visible to admins, not end users.")
        }
        StepList(colors, listOf(
            "New audiobooks are created in **DRAFT** status (shown as a yellow badge).",
            "While in DRAFT, add all episodes, upload cover images, and verify all metadata is correct.",
            "Draft audiobooks are **not visible** in the client/mobile app — only admins can see them.",
            "When ready, click the **\"Publish\"** button next to the status badge in the audiobook detail view.",
            "Once published, the audiobook becomes visible to end users immediately.",
            "The status badge changes from **DRAFT** (yellow) to **PUBLISHED** (green).",
            "You can filter the audiobook list by status using the **Draft** badge shown on list items."
        ))

        GuideCard(colors, "Status Overview") {
            CodeBlock(colors, """DRAFT       → Audiobook is being prepared (not visible to users)
PUBLISHED   → Audiobook is live and visible to all users
ARCHIVED    → Audiobook is hidden from users (can be restored)""")
        }

        // Translations
        SectionHeader(colors, "8", "Managing Translations")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("Castly supports multilingual content. You can add translations for audiobook episodes in Arabic, English, French, Spanish, and German.")
        }
        StepList(colors, listOf(
            "Open an audiobook and go to the **\"CHAPTERS\"** tab.",
            "Click on an episode row to open the edit panel.",
            "Switch to the **\"Translations\"** section in the edit panel.",
            "Select a language tab and fill in the translated title and description.",
            "Save the translation. A status badge shows completeness per language."
        ))

        // CSV Format Reference
        SectionHeader(colors, "9", "CSV Format Reference & Sample Download")

        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("The CSV file should use comma-separated values with a header row. Column names are case-insensitive.")
        }

        // Download button
        Div({ style { marginBottom(Spacing.lg) } }) {
            A(href = "$apiBaseUrl/audiobook_sample.csv", attrs = {
                attr("download", "audiobook_sample.csv")
                attr("target", "_blank")
                style {
                    padding(12.px, Spacing.xl)
                    backgroundColor(Color(colors.primary))
                    color(Color("#ffffff"))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    fontSize(Typography.base)
                    fontWeight("600")
                    property("text-decoration", "none")
                    display(DisplayStyle.InlineBlock)
                }
            }) {
                Text("Download Sample CSV")
            }
        }

        // CSV Columns Table
        Div({
            style {
                overflow("auto")
                marginBottom(Spacing.xl)
                borderRadius(BorderRadius.md)
                border(1.px, LineStyle.Solid, Color(colors.border))
            }
        }) {
            Table({
                style {
                    width(100.percent)
                    property("border-collapse", "collapse")
                    fontSize(Typography.sm)
                }
            }) {
                Thead {
                    Tr({
                        style {
                            backgroundColor(Color(colors.surfaceHover))
                        }
                    }) {
                        listOf("Column", "Required", "Description", "Example").forEach { header ->
                            Th({
                                style {
                                    padding(10.px, 12.px)
                                    textAlign("left")
                                    fontWeight("600")
                                    color(Color(colors.text))
                                    property("border-bottom", "2px solid ${colors.border}")
                                    property("white-space", "nowrap")
                                }
                            }) { Text(header) }
                        }
                    }
                }
                Tbody {
                    CsvColumnRow(colors, "ISBN", "Yes", "Unique ISBN identifier (also accepts ProductId)", "9781234567890")
                    CsvColumnRow(colors, "Title", "Yes", "Audiobook title (Arabic or primary language)", "كتاب المثال")
                    CsvColumnRow(colors, "Author", "Yes", "Author name", "أحمد محمد")
                    CsvColumnRow(colors, "Subtitle", "No", "Subtitle in primary language", "عنوان فرعي")
                    CsvColumnRow(colors, "TitleENGL", "No", "English title translation", "The Example Book")
                    CsvColumnRow(colors, "SubtitleENGL", "No", "English subtitle translation", "A Subtitle")
                    CsvColumnRow(colors, "AuthorBiography", "No", "Author biography text", "كاتب ومؤلف...")
                    CsvColumnRow(colors, "ReadBy", "No", "Narrator/Reader name (also accepts ReadBy_1)", "خالد عبدالله")
                    CsvColumnRow(colors, "Publisher", "No", "Publisher name", "دار النشر العربية")
                    CsvColumnRow(colors, "TotalDuration", "No", "Duration in HH:MM:SS, MM:SS, or seconds", "02:30:45")
                    CsvColumnRow(colors, "ProductLanguage", "No", "Language code", "ara")
                    CsvColumnRow(colors, "OriginalLanguage", "No", "Original language code", "eng")
                    CsvColumnRow(colors, "ProductInfoText", "No", "Description/synopsis", "وصف تفصيلي...")
                    CsvColumnRow(colors, "CopyrightYear", "No", "Copyright year", "2024")
                    CsvColumnRow(colors, "ReleaseDate", "No", "Release date (YYYY-MM-DD, MM/dd/yyyy, dd/MM/yyyy)", "2024-01-15")
                    CsvColumnRow(colors, "GenreTextBisac1", "No", "Genre path separated by /", "FICTION / General")
                    CsvColumnRow(colors, "Keywords", "No", "Comma-separated keywords/tags", "رواية, أدب عربي")
                }
            }
        }

        // RSS Format Reference
        SectionHeader(colors, "10", "RSS Feed Format Reference")
        P({ style { color(Color(colors.text)); lineHeight("1.6"); marginBottom(Spacing.md) } }) {
            Text("The RSS feed should follow the standard podcast RSS 2.0 format with enclosures for audio files. iTunes namespace tags are supported for additional metadata.")
        }

        GuideCard(colors, "Supported RSS Tags") {
            CodeBlock(colors, """<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0"
     xmlns:itunes="http://www.itunes.com/dtds/podcast-1.0.dtd">
  <channel>
    <title>Audiobook Title</title>
    <description>Audiobook description</description>
    <itunes:author>Author Name</itunes:author>
    <itunes:image href="https://example.com/cover.jpg"/>
    <item>
      <title>Chapter 1 - Introduction</title>
      <enclosure url="https://example.com/ch01.mp3"
                 type="audio/mpeg"
                 length="15000000"/>
      <itunes:duration>1845</itunes:duration>
      <itunes:episode>1</itunes:episode>
    </item>
    <item>
      <title>Chapter 2 - The Journey Begins</title>
      <enclosure url="https://example.com/ch02.mp3"
                 type="audio/mpeg"
                 length="18000000"/>
      <itunes:duration>2100</itunes:duration>
      <itunes:episode>2</itunes:episode>
    </item>
  </channel>
</rss>""")
        }

        // Footer note
        Div({
            style {
                marginTop(Spacing.xxl)
                padding(Spacing.lg)
                backgroundColor(Color(if (colors == LightColors) "#f0f9ff" else "#0c2d48"))
                borderRadius(BorderRadius.md)
                property("border-left", "4px solid ${colors.primary}")
            }
        }) {
            P({
                style {
                    margin(0.px)
                    color(Color(colors.text))
                    lineHeight("1.6")
                    fontSize(Typography.sm)
                }
            }) {
                B { Text("Need help? ") }
                Text("Check the Import History page to review past imports and diagnose errors. Each import log includes detailed error reports with row numbers and failure reasons.")
            }
        }
    }
}

@Composable
private fun SectionHeader(colors: com.castly.admin.ui.theme.AppColors, number: String, title: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.md)
            marginTop(Spacing.xxl)
            marginBottom(Spacing.md)
        }
    }) {
        Div({
            style {
                width(32.px)
                height(32.px)
                borderRadius(50.percent)
                backgroundColor(Color(colors.primary))
                color(Color("#ffffff"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontWeight("700")
                fontSize(Typography.sm)
                property("flex-shrink", "0")
            }
        }) { Text(number) }
        H3({
            style {
                margin(0.px)
                fontSize(Typography.lg)
                fontWeight("600")
                color(Color(colors.text))
            }
        }) { Text(title) }
    }
}

@Composable
private fun StepList(colors: com.castly.admin.ui.theme.AppColors, steps: List<String>) {
    Div({
        style {
            paddingLeft(Spacing.lg)
            marginBottom(Spacing.lg)
        }
    }) {
        steps.forEachIndexed { index, step ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.md)
                    marginBottom(Spacing.sm)
                    lineHeight("1.6")
                }
            }) {
                Span({
                    style {
                        color(Color(colors.textMuted))
                        fontWeight("600")
                        property("flex-shrink", "0")
                        width(24.px)
                        textAlign("right")
                    }
                }) { Text("${index + 1}.") }
                Span({
                    style {
                        color(Color(colors.text))
                    }
                    ref { element ->
                        // Render bold markers
                        val html = step
                            .replace(Regex("\\*\\*(.+?)\\*\\*")) { "<strong>${it.groupValues[1]}</strong>" }
                        element.innerHTML = html
                        onDispose { }
                    }
                }) {}
            }
        }
    }
}

@Composable
private fun GuideCard(colors: com.castly.admin.ui.theme.AppColors, title: String, content: @Composable () -> Unit) {
    Div({
        style {
            backgroundColor(Color(if (colors == LightColors) "#f8fafc" else "#1a1f2e"))
            borderRadius(BorderRadius.md)
            padding(Spacing.lg)
            marginBottom(Spacing.lg)
            border(1.px, LineStyle.Solid, Color(colors.border))
        }
    }) {
        B({
            style {
                color(Color(colors.text))
                fontSize(Typography.sm)
            }
        }) { Text(title) }
        content()
    }
}

@Composable
private fun CodeBlock(colors: com.castly.admin.ui.theme.AppColors, code: String) {
    Pre({
        style {
            backgroundColor(Color(if (colors == LightColors) "#1f2937" else "#0d1117"))
            color(Color("#e5e7eb"))
            padding(Spacing.lg)
            borderRadius(BorderRadius.md)
            overflow("auto")
            fontSize(13.px)
            lineHeight("1.5")
            property("font-family", "'Monaco', 'Menlo', 'Courier New', monospace")
            marginTop(Spacing.md)
            marginBottom(0.px)
        }
    }) {
        Code { Text(code) }
    }
}

@Composable
private fun CsvColumnRow(colors: com.castly.admin.ui.theme.AppColors, name: String, required: String, description: String, example: String) {
    Tr({
        style {
            property("border-bottom", "1px solid ${colors.border}")
        }
    }) {
        Td({
            style {
                padding(8.px, 12.px)
                fontWeight("600")
                color(Color(colors.text))
                property("font-family", "'Monaco', 'Menlo', 'Courier New', monospace")
                fontSize(12.px)
                property("white-space", "nowrap")
            }
        }) { Text(name) }
        Td({
            style {
                padding(8.px, 12.px)
                textAlign("center")
            }
        }) {
            Span({
                style {
                    padding(2.px, 8.px)
                    borderRadius(BorderRadius.sm)
                    fontSize(11.px)
                    fontWeight("600")
                    if (required == "Yes") {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        color(Color(colors.danger))
                    } else {
                        backgroundColor(Color(colors.surfaceHover))
                        color(Color(colors.textMuted))
                    }
                }
            }) { Text(required) }
        }
        Td({
            style {
                padding(8.px, 12.px)
                color(Color(colors.text))
                fontSize(Typography.sm)
            }
        }) { Text(description) }
        Td({
            style {
                padding(8.px, 12.px)
                color(Color(colors.textMuted))
                fontSize(12.px)
                property("font-family", "'Monaco', 'Menlo', 'Courier New', monospace")
            }
        }) { Text(example) }
    }
}
